import { API_BASE_URL } from '../config/constants';
import type { SessionManager } from '@/lib/session';
import { SessionState, isAuthenticatedState } from '@/lib/session';
import { userSession, adminSession } from '@/lib/session/instances';
import type { ServerDirective } from '@/lib/directives';
import { DirectiveKind } from '@/lib/directives';
import { logger } from '@/lib/utils/logger';

const KNOWN_DIRECTIVE_KINDS: ReadonlySet<DirectiveKind> = new Set<DirectiveKind>(
  Object.values(DirectiveKind),
);

interface BackendErrorBody {
  detail?: unknown;
}
interface BackendErrorDetail {
  directive?: string;
  message?: string;
  redirect_to?: string;
  redirectTo?: string;
  cta_text?: string;
  ctaText?: string;
  cta_url?: string;
  ctaUrl?: string;
  setup_path?: string;
  setupPath?: string;
}

function getDetail(body: unknown): BackendErrorDetail | string | null {
  if (body === null || typeof body !== 'object') return null;
  const { detail } = body as BackendErrorBody;
  if (detail === undefined || detail === null) return null;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') return detail as BackendErrorDetail;
  return null;
}

function extractMessage(body: unknown): string | undefined {
  const detail = getDetail(body);
  if (typeof detail === 'string') return detail;
  return detail?.message;
}

function mapStatusBodyToDirectiveKind(status: number, body: unknown): DirectiveKind | null {
  const detail = getDetail(body);
  if (detail !== null && typeof detail === 'object') {
    const tag = detail.directive;
    if (tag !== undefined && KNOWN_DIRECTIVE_KINDS.has(tag as DirectiveKind)) {
      return tag as DirectiveKind;
    }
  }
  if (status === 402) return DirectiveKind.SubscriptionExpired;
  if (status === 403) return DirectiveKind.PermissionDenied;
  return null;
}

function defaultMessageForKind(kind: DirectiveKind): string {
  switch (kind) {
    case DirectiveKind.SubscriptionExpired:
      return 'Your subscription has expired.';
    case DirectiveKind.TierGated:
      return 'This feature requires a higher tier.';
    case DirectiveKind.PermissionDenied:
      return 'You do not have permission to perform this action.';
    case DirectiveKind.PostRefresh401:
      return 'Access denied.';
    case DirectiveKind.ManagedAccessUnavailable:
      return 'Access through your managing organization is unavailable.';
    default:
      return 'Request failed.';
  }
}

function pickDirectiveFields(body: unknown): {
  redirectTo?: string;
  ctaText?: string;
  ctaUrl?: string;
  setupPath?: string;
  detail?: unknown;
} {
  const detail = getDetail(body);
  if (detail === null || typeof detail !== 'object') return {};
  return {
    redirectTo: detail.redirect_to ?? detail.redirectTo,
    ctaText: detail.cta_text ?? detail.ctaText,
    ctaUrl: detail.cta_url ?? detail.ctaUrl,
    setupPath: detail.setup_path ?? detail.setupPath,
    detail,
  };
}

export type ApiErrorType =
  | 'NetworkError'
  | 'ValidationError'
  | 'AuthenticationError'
  | 'AuthorizationError'
  | 'NotFoundError'
  | 'ConflictError'
  | 'QuotaExceededError'
  | 'ServerError'
  | 'UnknownError';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly type: ApiErrorType,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static fromResponse(response: Response, body?: unknown): ApiError {
    let message: string;

    if (typeof body === 'object' && body && 'detail' in body) {
      const { detail } = body as { detail: unknown };
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Handle validation errors that come as arrays
        message = detail
          .map((err: unknown) => {
            if (typeof err === 'object' && err && 'msg' in err) {
              return (err as { msg: string }).msg;
            }
            return String(err);
          })
          .join(', ');
      } else if (typeof detail === 'object' && detail) {
        // `detail` is an object — for DomainErrors this is the machine-readable
        // directive object (the DirectiveHandler reads `detail.directive` from the
        // body). Prefer the human-readable top-level `message` for display; only
        // stringify the object as a last resort. Without this, gating errors like
        // quota (402, which carry a directive) surface as raw JSON in the UI.
        message =
          'message' in body
            ? String((body as { message: unknown }).message)
            : JSON.stringify(detail);
      } else {
        message = String(detail);
      }
    } else if (typeof body === 'object' && body && 'message' in body) {
      message = String((body as { message: unknown }).message);
    } else if (typeof body === 'string') {
      message = body;
    } else {
      message = `HTTP ${response.status}: ${response.statusText}`;
    }

    let type: ApiErrorType;
    switch (response.status) {
      case 400:
      case 422:
        type = 'ValidationError';
        break;
      case 401:
        type = 'AuthenticationError';
        break;
      case 402:
        type = 'QuotaExceededError';
        break;
      case 403:
        type = 'AuthorizationError';
        break;
      case 404:
        type = 'NotFoundError';
        break;
      case 409:
        type = 'ConflictError';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        type = 'ServerError';
        break;
      default:
        type = 'UnknownError';
    }

    return new ApiError(message, response.status, type, body);
  }
}

export interface RequestOptions extends RequestInit {
  // Bypass for public endpoints (login / signup / register / csrf-token /
  // password-reset / etc.). Skips the awaitReady gate, the 401 anomaly
  // branch, and the CSRF-token attach.
  skipAuth?: boolean;
  organizationId?: string;
  // Response body decoding. Defaults to 'json'. Use 'blob' for binary /
  // streamed downloads (file exports) so they go through the SAME session
  // gate + single-flight 401 recovery + directive handling as every other
  // request, instead of a parallel raw-fetch path (CU-868kykvrx).
  responseType?: 'json' | 'blob';
}

export class ApiClient {
  private csrfToken: string | null = null;
  private _session: SessionManager | null;
  private _directiveSubscribers = new Set<(d: ServerDirective) => void>();

  constructor(session?: SessionManager) {
    this._session = session ?? null;
  }

  /**
   * Subscribe to directives this ApiClient emits (401 post-refresh,
   * 402 / 403 status responses). Consumed by DirectiveHandler. Returns
   * an unsubscribe function.
   */
  onDirective(cb: (d: ServerDirective) => void): () => void {
    this._directiveSubscribers.add(cb);
    return () => {
      this._directiveSubscribers.delete(cb);
    };
  }

  private emitDirective(d: ServerDirective): void {
    for (const cb of [...this._directiveSubscribers]) {
      try {
        cb(d);
      } catch (err) {
        logger.warn('ApiClient directive subscriber threw', {
          kind: d.kind,
          err,
        });
      }
    }
  }

  private async getCsrfToken(): Promise<string> {
    if (this.csrfToken) {
      return this.csrfToken;
    }

    try {
      // First try to get CSRF token from cookie
      const match = document.cookie.match(/coldsea_csrf_token=([^;]+)/);
      if (match && match[1]) {
        // eslint-disable-next-line prefer-destructuring
        this.csrfToken = match[1];
        return this.csrfToken;
      }

      // If no cookie, try fetching from endpoint (which may fail if not authenticated)
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/secure/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        // If the endpoint fails, we might not be authenticated yet
        // Return empty string to allow request to proceed
        return '';
      }

      const data = (await response.json()) as { csrf_token: string };
      this.csrfToken = data.csrf_token;
      return this.csrfToken;
    } catch {
      // CSRF token fetch failed - return empty string to allow request to proceed
      return '';
    }
  }

  async request<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const {
      skipAuth,
      organizationId: requestOrgId,
      responseType = 'json',
      ...fetchOptions
    } = options;

    // Initialize headers object if not present
    const headers: Record<string, string> = {};

    // Copy existing headers if they exist
    if (fetchOptions.headers) {
      if (fetchOptions.headers instanceof Headers) {
        fetchOptions.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(fetchOptions.headers)) {
        fetchOptions.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, fetchOptions.headers);
      }
    }

    // Set default Content-Type first
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    // httpOnly cookies handle authentication automatically via credentials: 'include'

    // Ensure we have CSRF token for non-GET requests. Public callers (login,
    // signup, register, password-reset, etc.) pass skipAuth: true, which also
    // bypasses CSRF — they don't have a token yet and the endpoints don't
    // require one.
    if (!skipAuth && fetchOptions.method && fetchOptions.method !== 'GET') {
      const csrfToken = await this.getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // X-Tenant-ID is sent only when explicitly requested (login flows where
    // no cookie exists yet). Routine authenticated requests rely on the
    // session cookie's signed org_id as the sole source of truth.
    if (requestOrgId) {
      headers['X-Tenant-ID'] = requestOrgId;
    }

    // Set the headers on fetchOptions
    fetchOptions.headers = headers;

    // Set defaults
    fetchOptions.credentials = fetchOptions.credentials || 'include';
    // Bypass HTTP cache: responses depend on the auth cookie and tenant
    // context, which the browser cache cannot key on. After an org switch,
    // a cached /auth/me would otherwise return the previous tenant.
    fetchOptions.cache = fetchOptions.cache || 'no-store';

    // PHASE 2 GATE: if constructed with a SessionManager, gate non-public
    // calls on session readiness. Tasks 5-7 add the full 401 / 402 / 403 /
    // org-error branches.
    if (this._session && !skipAuth) {
      const ready = await this._session.awaitReady();
      if (ready === SessionState.Anonymous || ready === SessionState.Dead) {
        throw new ApiError('No session', 401, 'AuthenticationError');
      }
    }

    // Make request
    let response: Response;
    try {
      response = await this.rawFetch(url, fetchOptions);
    } catch (error) {
      throw new ApiError('Network request failed', 0, 'NetworkError', error);
    }

    // 401 handling: funnel into the anomaly-recovery path when the session
    // believes it's alive. Org-context failures surface as 403 directives
    // (org_disabled / org_selection_required), never as 401s, so there is no
    // org short-circuit here — a 401 is always a session concern.
    if (response.status === 401 && !skipAuth && this._session) {
      // A sibling request may have already flipped the session to
      // `Refreshing` (concurrent 401s the moment the access token expires).
      // Both `Alive` and `Refreshing` must funnel into reportAnomaly401: it
      // single-flights the refresh and hands every caller the same in-flight
      // promise, so the sibling awaits that refresh and retries instead of
      // gating out and throwing a raw 401 — which AuthErrorBoundary would
      // categorize as authentication failure and force a full logout, even
      // though the refresh just succeeded. Only Anonymous/Dead skip recovery.
      if (isAuthenticatedState(this._session.state)) {
        const refreshed = await this._session.reportAnomaly401();
        if (refreshed) {
          // Retry once. Bypass request() to avoid re-gating via awaitReady.
          try {
            response = await this.rawFetch(url, fetchOptions);
          } catch (error) {
            throw new ApiError('Network request failed', 0, 'NetworkError', error);
          }
          if (response.status === 401) {
            // Session is provably alive (refresh succeeded). A 401 here is
            // authorization, not authentication — emit a directive and throw.
            const retryBody = (await response
              .clone()
              .json()
              .catch(() => null)) as unknown;
            const fields = pickDirectiveFields(retryBody);
            this.emitDirective({
              kind: DirectiveKind.PostRefresh401,
              message:
                extractMessage(retryBody) ?? defaultMessageForKind(DirectiveKind.PostRefresh401),
              domain: this._session.domain,
              ...fields,
            });
            throw new ApiError(
              extractMessage(retryBody) ?? defaultMessageForKind(DirectiveKind.PostRefresh401),
              401,
              'AuthorizationError',
              retryBody,
            );
          }
        }
        // refresh failed → session.state is now 'dead'; next awaitReady fails-fast.
        // For this in-flight request, fall through to ApiError.fromResponse.
      }
    }

    // 402 / 403 directive emission. Map body → typed DirectiveKind and
    // emit; fall through to ApiError so callers still see a thrown error.
    if ((response.status === 402 || response.status === 403) && this._session) {
      const directiveBody = (await response
        .clone()
        .json()
        .catch(() => null)) as unknown;

      // Org-context 403s (org_disabled / org_selection_required) carry a typed
      // `detail.directive`, so mapStatusBodyToDirectiveKind resolves them
      // ahead of the generic 403 → permission_denied fallback. 402 is
      // subscription_expired only; no org context applies.
      const kind = mapStatusBodyToDirectiveKind(response.status, directiveBody);
      if (kind) {
        const fields = pickDirectiveFields(directiveBody);
        this.emitDirective({
          kind,
          message: extractMessage(directiveBody) ?? defaultMessageForKind(kind),
          domain: this._session.domain,
          ...fields,
        });
      }
      // Fall through to ApiError.fromResponse below.
    }

    // Handle success
    if (response.ok) {
      if (response.status === 204) {
        return null as T;
      }

      if (responseType === 'blob') {
        return (await response.blob()) as T;
      }

      try {
        return (await response.json()) as T;
      } catch {
        return null as T;
      }
    }

    // Handle other errors
    const body = (await response.json().catch(() => null)) as unknown;
    throw ApiError.fromResponse(response, body);
  }

  /**
   * Lowest-level fetch helper. Used by request() and its single 401
   * retry path. Does NOT gate on session, does NOT process status codes
   * — callers handle that. Centralizing here keeps the retry path from
   * going back through request() and double-gating via awaitReady.
   */
  private async rawFetch(url: string, fetchOptions: RequestInit): Promise<Response> {
    return fetch(`${API_BASE_URL}${url}`, fetchOptions);
  }

  // Convenience methods
  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, { ...options, method: 'GET' });
  }

  async post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, { ...options, method: 'DELETE' });
  }

  /**
   * GET a binary / streamed response (file exports) as a Blob. Thin wrapper over
   * request() with responseType: 'blob', so downloads inherit the session gate,
   * single-flight 401 refresh recovery, directive handling, and typed
   * ApiError-on-non-ok — no parallel raw-fetch path (CU-868kykvrx). The
   * `organizationId` option is forwarded to request() (X-Tenant-ID) unchanged.
   */
  async getBlob(url: string, options?: RequestOptions): Promise<Blob> {
    return this.request<Blob>(url, { ...options, method: 'GET', responseType: 'blob' });
  }
}

// Per-domain singletons. userApiClient is bound to userSession (user/org
// domain); adminApiClient is bound to adminSession (system-admin domain).
// Each instance gates requests on its own SessionManager and emits
// directives tagged with its domain.
export const userApiClient = new ApiClient(userSession);
export const adminApiClient = new ApiClient(adminSession);
